import { useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import * as ContentListModal from '@/components/ContentListModal';
import { ModalVariant } from '@/components/Modals/types';
import usePagination from '@/hooks/usePagination';
import contentConfig from '@/modules/content/config';
import * as LearningContent from '@/modules/corporate/components/LearningContent';
import { ILearningContent } from '@/modules/corporate/components/LearningContent/types';
import { useGetBatchUsersProgressQuery } from '@/modules/journey/views/JourneyDetails/graphql/queries/generated/getBatchUsersProgress';
import { GroupAdditionMethodEnum, UserType } from '@/types/schema';

import useJourneyContentPerformances from '../../../../hooks/useJourneyContentPerformances';
import UserListItem from './components/UserListItem';

export type JourneyParticipantProgressModalProps = {
  content: ILearningContent;
  onClose: () => void;
  batchId: string;
  journeyStepId: string;
  isSelectAll?: boolean;
  participantIds?: string[] | null;
  excludeParticipantIds?: string[] | null;
  isEditMode?: boolean;
  sessionId?: string;
};
const JourneyParticipantProgressModal = (props: JourneyParticipantProgressModalProps) => {
  const { content, onClose, batchId, journeyStepId } = props;
  const { t } = useTranslation(['Journey']);
  const [searchText, setSearchText] = useState('');

  const { data: participants, fetchMore } = usePagination(
    useGetBatchUsersProgressQuery,
    {
      filters: {
        groupFilter: {
          groupIds: [batchId],
          groupAdditionMethod: GroupAdditionMethodEnum.All,
        },
        searchText,
        journeyStepIdForPersonaView: journeyStepId,
      },
      journeyStepId,
      batchId,
    },
    { edgeKey: 'users', requestPolicy: 'cache-and-network' },
  );

  const userIds = participants.map((user) => user.id);

  const { data: contentPerformances } = useJourneyContentPerformances(content.id, userIds, false);

  if (!batchId.length || !journeyStepId.length) {
    return null;
  }

  return (
    <ContentListModal.Main
      fetchMore={fetchMore}
      header={{
        title: content.name,
        description: t(contentConfig[content.type!].label.title),
        Icon: () => <LearningContent.Icon className="" height={56} width={56} />,
      }}
      items={participants as UserType[]}
      searchBar={{
        value: searchText,
        onChange: setSearchText,
      }}
      variant={ModalVariant.Wide}
      onClose={onClose}>
      <ContentListModal.Body
        className="flex-1 px-16"
        itemRenderer={(index: number) => {
          const user = participants[index];

          return (
            <UserListItem
              key={user.id}
              batchId={batchId}
              entityId={content.id}
              progress={contentPerformances?.[user.id]?.progress ?? 0}
              user={user}
            />
          );
        }}></ContentListModal.Body>
    </ContentListModal.Main>
  );
};

export default JourneyParticipantProgressModal;
